import { useContext, useEffect, useState } from "react";
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { v4 } from "uuid";

import type {
  SuccessPatientIndexSearchResultV2,
  TooManyMatchesPatientIndexSearchResultV2,
} from "@thalamos/aspire-common";

import type { CriteriaType } from "../PatientSearchV2/CollectCriteria.js";
import { CollectCriteria } from "../PatientSearchV2/CollectCriteria.js";
import type { CollectCriteriaDemographicsProps } from "../PatientSearchV2/CollectCriteriaDemographics.js";
import type { CollectCriteriaNhsNumberProps } from "../PatientSearchV2/CollectCriteriaNhsNumber.js";
import { SearchResults } from "../PatientSearchV2/SearchResults.js";
import { ageRangeToDateRange } from "../PatientSearchV2/util.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { HorizontalLine } from "~/components/form/HorizontalLine.js";
import { PatientBanner } from "~/components/layout/index.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { usePatientTimeline } from "~/hooks/apiCalls.js";
import { routeFns } from "~/routePaths.js";

type PatientMergeSearchProps = Record<string, never>;

type SearchResultsAndHandlers = {
  searchResults:
    | "loading"
    | TooManyMatchesPatientIndexSearchResultV2
    | SuccessPatientIndexSearchResultV2;

  /**
   * The function we will call if we want to continue with a search result.
   * @param index The index of the selected search result.
   * @returns
   */
  onContinue?: (index: number) => void;
};

export function PatientMergeSearch(_props: PatientMergeSearchProps) {
  const userContext = useContext(LoggedInUserContext);
  const hasPatientMergeAccess =
    userContext?.user?.hasPatientMergeAccess ?? false;
  const hasPhoneticMatchAccess =
    userContext?.user?.sessionOrganisationConfiguration
      ?.phoneticPatientIndexSearchEnabled ?? false;
  const canSearchByNhsNumber =
    userContext?.user.sessionOrganisationConfiguration?.nhsNumberEnabled ??
    false;

  const { fromPatientId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Redirect away if user doesn't have merge access
  useEffect(() => {
    if (userContext && !hasPatientMergeAccess) {
      renderErrorToast({
        message: "You do not have access to merge patients",
      });
      navigate(routeFns.home());
    }
  }, [userContext]);

  const {
    patientTimeline: fromPatientTimeline,
    patientTimelineCompleteLoading: fromPatientTimelineLoading,
    reloadPatientTimeline,
  } = usePatientTimeline({
    patientId: fromPatientId,
  });

  const [criteriaType, setCriteriaType] = useState<CriteriaType>("nhs-number");

  const [searchResultsAndHandlers, setSearchResultsAndHandlers] = useState<
    SearchResultsAndHandlers | undefined
  >(undefined);

  // This is where we store the index of the selected search
  const [searchResultIndexSelected, setSearchResultIndexSelected] = useState<
    number | null
  >(null);

  const state =
    searchResultsAndHandlers === undefined
      ? "search"
      : searchResultsAndHandlers.searchResults === "loading"
        ? "searching"
        : searchResultsAndHandlers.searchResults.searchOutcome ===
            "too-many-matches"
          ? "too-many-matches"
          : searchResultsAndHandlers.searchResults.pdsResult ===
              "too-many-matches"
            ? "too-many-matches-pds"
            : searchResultsAndHandlers.searchResults.matchedPatients.length ===
                0
              ? "results-not-found"
              : searchResultIndexSelected === null
                ? "results-found"
                : "result-selected";
  const tooManyMatches = state === "too-many-matches";

  const onContinueFn =
    (data: SuccessPatientIndexSearchResultV2) => async (index: number) => {
      const patient = data.matchedPatients[index];
      if (patient === undefined) return;

      const searchReasoningResult = await api.patients.addSearchV2Reasoning(
        patient.resultId,
        {
          reason: "Patient Merge",
        },
      );

      if (searchReasoningResult.status !== 200) {
        renderErrorToast({
          message: "Failed to add search reasoning - please try again later",
        });
        return;
      }

      if (searchReasoningResult.data.type === "patient-page") {
        const toPatientId = searchReasoningResult.data.patientId;

        if (toPatientId) {
          navigate(
            routeFns.patientMergeConfirmation(fromPatientId!, toPatientId),
          );
        }
      }
    };

  // Search for patients using NHS Number
  const searchByNhsNumber: CollectCriteriaNhsNumberProps["onSubmit"] = async (
    values,
  ) => {
    setSearchResultsAndHandlers({ searchResults: "loading" });

    // Look for an existing aspire patient with this NHS Number
    const { data } = await api.patients.searchV2(v4(), {
      type: "nhs-number",
      nhsNumber: values.nhsNumber,
      patientSearchVariant: "merge",
      patientIdToExclude: fromPatientId!,
    });

    if (data.searchOutcome === "too-many-matches") {
      setSearchResultsAndHandlers({ searchResults: data });
      return;
    }
    setSearchResultsAndHandlers({
      searchResults: data,
      onContinue: onContinueFn(data),
    });
  };

  // Search for patients using Demographics
  const searchByDemographics: CollectCriteriaDemographicsProps["onSubmit"] =
    async (values) => {
      setSearchResultsAndHandlers({ searchResults: "loading" });

      const { data } = await api.patients.searchV2(v4(), {
        type: "demographics",
        enablePhoneticMatchOnSurname: values.usePhoneticSurnameMatch,
        enablePhoneticMatchOnGivenName: values.usePhoneticGivenNameMatch,

        given: values.givenName ?? undefined,
        surname: values.familyName!,
        birthdate: values.dateOfBirthIsExact
          ? values.dateOfBirthExact
          : ageRangeToDateRange(values.ageApprox),

        patientSearchVariant: "merge",
        patientIdToExclude: fromPatientId!,
      });

      if (data.searchOutcome === "too-many-matches") {
        setSearchResultsAndHandlers({ searchResults: data });
        return;
      }

      setSearchResultsAndHandlers({
        searchResults: data,
        onContinue: onContinueFn(data),
      });
    };

  return (
    <PopupDialog
      open={true}
      fullWidth
      onClose={() => {
        navigate(routeFns.patientHome(fromPatientId!));
      }}
    >
      <PopupDialogTitle
        titleText={`Merge Patient`}
        closeDialog={() => {
          navigate(routeFns.patientHome(fromPatientId!));
        }}
      />
      {fromPatientTimelineLoading || !fromPatientTimeline ? (
        <CircularProgress />
      ) : (
        <Stack>
          <PatientBanner
            patientTimeline={fromPatientTimeline}
            patient={fromPatientTimeline.patient}
            reloadPatientTimeline={reloadPatientTimeline}
            nhsNumber={fromPatientTimeline.patient.nhsNumber ?? undefined}
            disableSticky={false}
          />
          <Banner
            bannerType={BannerList.WARNING}
            title={"Please search for a patient to merge this patient into"}
          />
          <Stack sx={{ mt: "2rem" }}>
            {/* Criteria type selection */}
            <FormControl>
              <RadioGroup
                value={criteriaType}
                onChange={(event) => {
                  const value = event.target.value as CriteriaType;
                  if (["nhs-number", "demographics"].includes(value)) {
                    setCriteriaType(value);
                    setSearchResultsAndHandlers(undefined);
                    setSearchResultIndexSelected(null);
                  }
                }}
              >
                {canSearchByNhsNumber && (
                  <FormControlLabel
                    data-testid="radio-nhs-number"
                    value="nhs-number"
                    control={<Radio />}
                    label="Search by NHS Number"
                    disabled={["searching", "result-selected"].includes(state)}
                  />
                )}
                <FormControlLabel
                  data-testid="radio-demographics"
                  value="demographics"
                  control={<Radio />}
                  label="Search by Demographics"
                  disabled={["searching", "result-selected"].includes(state)}
                />
              </RadioGroup>
            </FormControl>
            <HorizontalLine />

            <CollectCriteria
              lastSearchedDemographics={undefined}
              canUsePhoneticNameMatch={hasPhoneticMatchAccess}
              onSubmitNhsNumber={searchByNhsNumber}
              onSubmitDemographics={searchByDemographics}
              onSubmitPdsDemographics={() => {}}
              criteriaType={criteriaType}
              disabled={["searching", "result-selected"].includes(state)}
            >
              {({ onClear }) => {
                return (
                  <>
                    <SearchResults
                      canSearchByPdsDemographics={false}
                      criteriaType={criteriaType}
                      setCriteriaType={setCriteriaType}
                      searchResult={
                        tooManyMatches
                          ? "too-many-matches"
                          : (searchResultsAndHandlers?.searchResults as SuccessPatientIndexSearchResultV2)
                      }
                      onSelectedIndexChanged={setSearchResultIndexSelected}
                    />
                    <FormFooterSection
                      disableSubmit={["searching"].includes(state)}
                      saveLabel={
                        [
                          "search",
                          "searching",
                          "results-not-found",
                          "too-many-matches",
                          "too-many-matches-pds",
                          "results-found",
                        ].includes(state)
                          ? t("buttonLabels.search")
                          : t("buttonLabels.continue")
                      }
                      discardLabel="Clear Search"
                      performSaveActionOnFormSubmit={[
                        "search",
                        "results-not-found",
                        "results-found",
                        "too-many-matches",
                        "too-many-matches-pds",
                      ].includes(state)}
                      onSave={
                        state === "result-selected"
                          ? () => {
                              if (searchResultIndexSelected !== null)
                                searchResultsAndHandlers?.onContinue?.(
                                  searchResultIndexSelected,
                                );
                            }
                          : () => {}
                      }
                      onCancel={() => {
                        setSearchResultsAndHandlers(undefined);
                        setSearchResultIndexSelected(null);
                        onClear();
                      }}
                    />
                  </>
                );
              }}
            </CollectCriteria>
          </Stack>
        </Stack>
      )}
    </PopupDialog>
  );
}
